import Box from "@mui/material/Box"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import Typography from "@mui/material/Typography"
import styles from "./SegmentedInput.module.scss"

// A side-by-side button choice for answers with two or three options, where a
// dropdown costs three taps for what should be one. Used for the Yes/No and
// Available/NA questions on the survey pages.
type Option = { id: string; label: string }

type SegmentedInputProps = {
    name: string
    label?: string
    value?: string
    onChange?: (event: { target: { name: string; value: string } }) => void
    options?: Option[]
    error?: boolean
    helperText?: any
    required?: boolean
    disabled?: boolean
    subLabel?: string
    labelFontWeight?: any
    style?: any
}

const SegmentedInput = (props: SegmentedInputProps) => {
    const options = props.options ?? []

    const select = (id: string) => {
        if (props.disabled) return
        // Shaped like a DOM change event so formik.handleChange consumes it directly.
        props.onChange?.({ target: { name: props.name, value: id } })
    }

    return (
        <Box sx={{ margin: props.style ? undefined : "16px 0" }}>
            <FormControl sx={{ padding: 0, width: "100%" }}>
                <FormLabel
                    disabled={props.disabled}
                    id={props.name}
                    sx={{ fontWeight: props.labelFontWeight, width: "100%" }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Typography>{props.label}</Typography>
                        {props.required && (
                            <Typography sx={{ color: "#E20000", fontSize: "14px", fontWeight: 500 }}>*</Typography>
                        )}
                    </Box>
                </FormLabel>

                <Box
                    className={styles.segmented}
                    role="radiogroup"
                    aria-labelledby={props.name}
                    aria-invalid={props.error || undefined}
                >
                    {options.map(option => {
                        const selected = props.value === option.id
                        return (
                            <button
                                key={option.id}
                                id={`${props.name}_${option.id}`}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                disabled={props.disabled}
                                className={[
                                    styles.segmented__option,
                                    selected ? styles.segmented__option_on : "",
                                    props.error ? styles.segmented__option_error : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                onClick={() => select(option.id)}
                            >
                                {option.label}
                            </button>
                        )
                    })}
                </Box>

                {props.subLabel && (
                    <Typography className={styles.segmented__sublabel}>{props.subLabel}</Typography>
                )}

                {props.error && props.helperText && (
                    <Typography className={styles.segmented__error}>{props.helperText}</Typography>
                )}
            </FormControl>
        </Box>
    )
}

export default SegmentedInput
